import { ApiClientError } from '@/lib/api/client'
import { refusalKey } from '@/lib/api/refusal-key'

/**
 * The lane B2 refusals, as catalogue keys. Never the API's English `message`
 * (CLAUDE.md rule 9): each `errorCode` in the contract reads its own sentence,
 * anything else falls back by status through refusalKey().
 */
export const GROUP_CATEGORY_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ['group_category_name_taken', 'groupCategories.errors.nameTaken'],
  ['group_category_not_found', 'groupCategories.errors.notFound'],
  ['group_category_exclusive_conflict', 'groupCategories.errors.exclusiveConflict'],
])

export const CUSTOM_FIELD_REPORTING_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ['custom_field_type_not_reportable', 'population.fieldReporting.errors.typeNotReportable'],
])

export const BREAKDOWN_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ['report_dimension_unknown', 'population.breakdown.errors.dimensionUnknown'],
])

/**
 * #829 — a group create / rename refused because another live group has that
 * name (409, groups.service.ts groupNameTaken). The forms put this sentence
 * under the Name field instead of a toast. Not part of the lane B2 contract
 * below (ALL_POPULATION_ERROR_KEYS): its own spec is
 * components/recipients/groups/__tests__/group-name-taken-manage-members-colour-dot.spec.tsx.
 */
export const GROUP_NAME_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ['group_name_taken', 'createGroup.nameTaken'],
])

/** Every errorCode the lane's contract names, for the catalogue spec. */
export const ALL_POPULATION_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ...GROUP_CATEGORY_ERROR_KEYS,
  ...CUSTOM_FIELD_REPORTING_ERROR_KEYS,
  ...BREAKDOWN_ERROR_KEYS,
])

const NAMED_CONFLICT_KEY = 'groupCategories.errors.exclusiveConflictNamed'

type Translate = (key: string, params?: Record<string, string | number>) => string

function bodyOf(error: unknown): Record<string, unknown> {
  return error instanceof ApiClientError && error.body && typeof error.body === 'object'
    ? (error.body as Record<string, unknown>)
    : {}
}

/**
 * The sentence for a refused group-category or group write. An exclusive
 * conflict says how many people are affected, and names the category when the
 * API says which one (adding people, moving a group into it).
 */
export function groupCategoryErrorMessage(error: unknown, t: Translate): string {
  const key = refusalKey(error, GROUP_CATEGORY_ERROR_KEYS)
  if (key !== 'groupCategories.errors.exclusiveConflict') return t(key)
  const body = bodyOf(error)
  const count = typeof body.conflicts === 'number' ? body.conflicts : 0
  const category = typeof body.categoryName === 'string' ? body.categoryName.trim() : ''
  return category ? t(NAMED_CONFLICT_KEY, { count, category }) : t(key, { count })
}

const MERGE_CONFLICT_KEY = 'groupCategories.errors.mergeExclusiveConflict'
const MERGE_CONFLICT_BINNED_KEY = 'groupCategories.errors.mergeExclusiveConflictBinned'

/**
 * The sentence for a refused group merge. An exclusive-category refusal names
 * its cause from the API's own fields: the category, how many people the
 * merge would put in two of its groups, and how many of them are in the
 * recycle bin (their memberships survive the bin and come back with them, but
 * no member list shows them, so the sentence has to). Any other refusal reads
 * the group-category sentence, or the generic one by status.
 */
export function groupMergeErrorMessage(error: unknown, t: Translate): string {
  if (!(error instanceof ApiClientError) || error.errorCode !== 'group_category_exclusive_conflict') {
    return t(refusalKey(error, GROUP_CATEGORY_ERROR_KEYS))
  }
  const body = bodyOf(error)
  const category = typeof body.categoryName === 'string' ? body.categoryName.trim() : ''
  if (!category) return groupCategoryErrorMessage(error, t)
  const count = typeof body.conflicts === 'number' ? body.conflicts : 0
  const binned = typeof body.binned === 'number' ? body.binned : 0
  return binned > 0
    ? t(MERGE_CONFLICT_BINNED_KEY, { category, count, binned })
    : t(MERGE_CONFLICT_KEY, { category, count })
}

/** True when `error` is one of the group-category refusals this lane added. */
export function isGroupCategoryRefusal(error: unknown): boolean {
  return error instanceof ApiClientError && !!error.errorCode && GROUP_CATEGORY_ERROR_KEYS.has(error.errorCode)
}

export function customFieldReportingErrorMessage(error: unknown, t: Translate): string | null {
  if (!(error instanceof ApiClientError) || !error.errorCode) return null
  const key = CUSTOM_FIELD_REPORTING_ERROR_KEYS.get(error.errorCode)
  return key ? t(key) : null
}

export function breakdownErrorMessage(error: unknown, t: Translate): string {
  const key = refusalKey(error, BREAKDOWN_ERROR_KEYS)
  return t(key === 'errors.generic' ? 'population.breakdown.errors.loadFailed' : key)
}

/**
 * #829 — the catalogue key to show under the Name field when the API refused
 * a group create / rename for a taken name, or null for any other failure.
 */
export function groupNameTakenKey(error: unknown): string | null {
  if (!(error instanceof ApiClientError) || !error.errorCode) return null
  return GROUP_NAME_ERROR_KEYS.get(error.errorCode) ?? null
}
